import { semieixos, type AberturaTanque, type PerfilTanque } from '../caminhaoTanque/perfil'

/**
 * Pose do tanque avariado e o que ela faz com o líquido dentro dele.
 *
 * O líquido fica sempre com a superfície horizontal; o que gira é o tanque.
 * Por isso uma mesma válvula pode estar no líquido com o caminhão sobre
 * rodas e no vapor com ele tombado — e é isso que decide por onde se tira
 * líquido e por onde se liga o retorno de vapor no transbordo.
 *
 * Rolagem em torno do eixo do tanque (+Z, para a frente): positiva levanta o
 * lado esquerdo (+X). Tombado sobre o lado esquerdo = −90°.
 * Arfagem: positiva baixa a frente.
 */

export type IdPose = 'sobreRodas' | 'tombadoEsquerdo' | 'tombadoDireito' | 'capotado'

export const POSES: Record<IdPose, { nome: string; rolagemGraus: number }> = {
  sobreRodas: { nome: 'Sobre rodas', rolagemGraus: 0 },
  tombadoEsquerdo: { nome: 'Tombado sobre o lado esquerdo', rolagemGraus: -90 },
  tombadoDireito: { nome: 'Tombado sobre o lado direito', rolagemGraus: 90 },
  capotado: { nome: 'Capotado', rolagemGraus: 180 },
}

export interface PoseTanque {
  pose: IdPose
  /** Inclinação longitudinal, graus (terreno em declive); positiva baixa a frente. */
  arfagemGraus: number
}

const rad = (graus: number) => (graus * Math.PI) / 180

/** Fração da área de um círculo unitário abaixo da reta horizontal y = t (t em [−1, 1]). */
export function fracaoAbaixoNoCirculo(t: number): number {
  const c = Math.min(1, Math.max(-1, t))
  return 1 - (Math.acos(c) - c * Math.sqrt(1 - c * c)) / Math.PI
}

/**
 * Meia-altura da seção na vertical do mundo, com o tanque girado: a
 * distância do eixo até a tangente horizontal (função suporte da elipse).
 */
export function meiaAlturaGirada(tanque: PerfilTanque, rolagemGraus: number): number {
  const [a, b] = semieixos(tanque)
  const r = rad(rolagemGraus)
  return Math.sqrt((a * Math.sin(r)) ** 2 + (b * Math.cos(r)) ** 2)
}

/**
 * Altura da superfície do líquido acima do eixo do tanque, para o enchimento
 * dado (0..1). Uma transformação afim leva a elipse girada num círculo e
 * preserva razões de área e retas paralelas: a fração abaixo da superfície só
 * depende do deslocamento normalizado pela meia-altura girada.
 */
export function nivelAcimaDoEixoM(tanque: PerfilTanque, rolagemGraus: number, enchimento: number): number {
  if (!(enchimento >= 0 && enchimento <= 1)) throw new Error('O enchimento vai de 0 a 100%.')
  let baixo = -1
  let alto = 1
  for (let i = 0; i < 60; i++) {
    const meio = (baixo + alto) / 2
    if (fracaoAbaixoNoCirculo(meio) < enchimento) baixo = meio
    else alto = meio
  }
  return ((baixo + alto) / 2) * meiaAlturaGirada(tanque, rolagemGraus)
}

/** Ponto da abertura relativo ao centro do tanque, já com rolagem e arfagem: [x, y, z]. */
export function pontoGirado(tanque: PerfilTanque, abertura: AberturaTanque, pose: PoseTanque): readonly [number, number, number] {
  const [a, b] = semieixos(tanque)
  const xl = Math.sin(abertura.anguloRad) * a
  const yl = Math.cos(abertura.anguloRad) * b
  const r = rad(POSES[pose.pose].rolagemGraus)
  const x = xl * Math.cos(r) - yl * Math.sin(r)
  const y = xl * Math.sin(r) + yl * Math.cos(r)
  const p = rad(pose.arfagemGraus)
  // arfagem em torno de +X: a frente (z > 0) desce quando p > 0
  return [x, y * Math.cos(p) - abertura.zM * Math.sin(p), y * Math.sin(p) + abertura.zM * Math.cos(p)]
}

/** Altura do eixo do tanque acima do chão: sobre rodas, a do perfil; senão, o costado apoiado no chão. */
export function alturaEixoNaPose(tanque: PerfilTanque, pose: PoseTanque): number {
  return pose.pose === 'sobreRodas' ? tanque.alturaEixoM : meiaAlturaGirada(tanque, POSES[pose.pose].rolagemGraus)
}

/** Abaixo disto do chão, a abertura fica prensada contra o solo. */
export const FOLGA_MINIMA_SOLO_M = 0.3

export type Fase = 'liquido' | 'vapor'

export interface SituacaoAbertura {
  abertura: AberturaTanque
  fase: Fase
  /** Altura da abertura acima do chão, m. */
  alturaSoloM: number
  obstruida: boolean
  /** Coluna de líquido sobre a abertura (positiva) ou distância até o líquido (negativa), m. */
  profundidadeM: number
}

export function situacaoDasAberturas(tanque: PerfilTanque, pose: PoseTanque, enchimento: number): SituacaoAbertura[] {
  const nivel = nivelAcimaDoEixoM(tanque, POSES[pose.pose].rolagemGraus, enchimento)
  const eixo = alturaEixoNaPose(tanque, pose)
  return tanque.aberturas.map((abertura) => {
    const [, y] = pontoGirado(tanque, abertura, pose)
    const profundidadeM = nivel - y
    const alturaSoloM = eixo + y
    return {
      abertura,
      // a superfície é horizontal: com arfagem, cada posição ao longo do eixo tem o seu "fundo",
      // mas o nível do líquido é o mesmo — aproximação válida enquanto o líquido não encosta nos tampos
      fase: profundidadeM > 0 ? 'liquido' : 'vapor',
      alturaSoloM,
      obstruida: alturaSoloM < FOLGA_MINIMA_SOLO_M,
      profundidadeM,
    }
  })
}
